/**
 * Energy Service
 * Handles all API calls related to energy performance certificates (EPC) and
 * energy efficiency data for properties
 */

import { apiRequest } from './api';
import config from '../config';

/**
 * Fetch EPC data for a specific address
 *
 * @param {Object} address - The address object containing postcode and address details
 * @returns {Promise<Object>} - Energy performance data
 */
const getEpcData = async (address, forceRefresh = false) => {
  // We need to encode the address for URL usage
  const addressQuery = encodeURIComponent(
    `${address.flat_number ? address.flat_number + ' ' : ''}${address.address}`,
  );

  // Include property_type and flat_number in the API call if available
  const propertyTypeParam = address.property_type
    ? `&property_type=${encodeURIComponent(address.property_type)}`
    : '';
  const flatNumberParam = address.flat_number
    ? `&flat_number=${encodeURIComponent(address.flat_number)}`
    : '';

  // Add a timestamp parameter to prevent caching when forceRefresh is true
  const timestampParam = forceRefresh ? `&_t=${Date.now()}` : '';

  const url = `${config.apiUrl}/energy/epc?postcode=${encodeURIComponent(
    address.postcode,
  )}&address=${addressQuery}${propertyTypeParam}${flatNumberParam}${timestampParam}`;

  // Create a cache key based on the address
  const cacheKey = forceRefresh ? null : `epc_${address.postcode}_${addressQuery}`;

  // EPC data doesn't change frequently, so we can cache it for longer
  const cacheDuration = 7 * 24 * 60 * 60 * 1000; // 7 days

  try {
    console.log(
      '%c Fetching EPC data',
      'background: #3182ce; color: white; padding: 2px 6px; border-radius: 2px;',
    );
    console.log('URL:', url);

    const data = await apiRequest(url, {});
    console.log(
      '%c Raw EPC API response:',
      'background: #2F855A; color: white; padding: 2px 6px; border-radius: 2px;',
      data,
    );

    // Transform API response to the format expected by the EnergyPerformance component
    if (data && data.property_data) {
      // Calculate total current and potential costs
      const currentCost =
        (data.property_data.heating_cost_current || 0) +
        (data.property_data.hot_water_cost_current || 0) +
        (data.property_data.lighting_cost_current || 0);

      const potentialCost =
        (data.property_data.heating_cost_potential || 0) +
        (data.property_data.hot_water_cost_potential || 0) +
        (data.property_data.lighting_cost_potential || 0);

      const potentialSaving = currentCost - potentialCost;

      console.log('Current energy costs:', {
        heating: data.property_data.heating_cost_current,
        hotWater: data.property_data.hot_water_cost_current,
        lighting: data.property_data.lighting_cost_current,
        total: currentCost,
      });

      console.log('Potential energy costs:', {
        heating: data.property_data.heating_cost_potential,
        hotWater: data.property_data.hot_water_cost_potential,
        lighting: data.property_data.lighting_cost_potential,
        total: potentialCost,
        saving: potentialSaving,
      });

      const formattedData = {
        epcData: data,
        // Format data for the EnergyPerformance component
        currentRating: data.property_data.current_energy_rating,
        potentialRating: data.property_data.potential_energy_rating,
        currentScore: data.property_data.current_energy_efficiency,
        potentialScore: data.property_data.potential_energy_efficiency,
        currentCost: currentCost,
        potentialCost: potentialCost,
        potentialSaving: potentialSaving,
        heatingCost: data.property_data.heating_cost_current,
        hotWaterCost: data.property_data.hot_water_cost_current,
        lightingCost: data.property_data.lighting_cost_current,
        heatingType: data.property_data.main_heating_description,
        floorArea: data.property_data.total_floor_area,
        glazingType: data.property_data.windows_description,
        constructionYear: data.property_data.construction_age_band || 'Unknown',
        propertyType: data.property_data.property_type,
        builtForm: data.property_data.built_form,
        mainFuel: data.property_data.main_fuel,
        roofDescription: data.property_data.roof_description,
        roofEnergy: data.property_data.roof_energy_eff,
        wallsDescription: data.property_data.walls_description,
        wallsEnergy: data.property_data.walls_energy_eff,
        windowsEnergy: data.property_data.windows_energy_eff,
        inspectionDate: data.property_data.inspection_date,
        recommendations: generateRecommendations(data),
        isLoading: false,
        error: null,
      };

      console.log(
        '%c Transformed EPC data:',
        'background: #DD6B20; color: white; padding: 2px 6px; border-radius: 2px;',
        formattedData,
      );
      return formattedData;
    } else {
      console.warn(
        '%c Missing or invalid EPC data structure:',
        'background: #E53E3E; color: white; padding: 2px 6px; border-radius: 2px;',
        data,
      );
    }

    return {
      epcData: data,
      isLoading: false,
      error: null,
    };
  } catch (error) {
    console.error(
      '%c Error fetching EPC data:',
      'background: #E53E3E; color: white; padding: 2px 6px; border-radius: 2px;',
      error,
    );
    return {
      epcData: null,
      isLoading: false,
      error: 'Failed to fetch energy performance data',
    };
  }
};

/**
 * Generate recommendations based on EPC data
 *
 * @param {Object} data - Full EPC data
 * @returns {Array} - Array of recommendation objects
 */
const generateRecommendations = (data) => {
  if (!data || !data.property_data) return [];

  const recommendations = [];
  const pd = data.property_data;

  // Add recommendations based on energy efficiency ratings
  if (pd.roof_energy_eff === 'Very Poor' || pd.roof_energy_eff === 'Poor') {
    recommendations.push({
      description: 'Improve roof insulation',
      saving: Math.round((pd.heating_cost_current || 0) * 0.15),
      cost: 1500,
    });
  }

  if (pd.walls_energy_eff === 'Very Poor' || pd.walls_energy_eff === 'Poor') {
    recommendations.push({
      description: 'Add wall insulation',
      saving: Math.round((pd.heating_cost_current || 0) * 0.2),
      cost: 2500,
    });
  }

  if (pd.windows_energy_eff === 'Very Poor' || pd.windows_energy_eff === 'Poor') {
    recommendations.push({
      description: 'Upgrade to full double glazing',
      saving: Math.round((pd.heating_cost_current || 0) * 0.1),
      cost: 3000,
    });
  }

  // If we have no specific recommendations but potential improvements exist
  if (recommendations.length === 0 && pd.potential_energy_rating !== pd.current_energy_rating) {
    recommendations.push({
      description: 'Improve overall energy efficiency',
      saving: Math.round((pd.heating_cost_current || 0) * 0.1),
      cost: 1000,
    });
  }

  return recommendations;
};

/**
 * Calculate potential energy savings based on EPC data
 *
 * @param {Object} epcData - Energy performance certificate data
 * @returns {Object} - Potential savings and recommendations
 */
const calculateEnergySavings = (epcData) => {
  if (!epcData || !epcData.property_data || !epcData.property_data.current_energy_rating) {
    return {
      potentialSavings: 0,
      recommendations: [],
    };
  }

  // Default savings based on EPC rating improvements
  const defaultSavingsByRating = {
    G: 1200,
    F: 1000,
    E: 800,
    D: 600,
    C: 400,
    B: 200,
    A: 0,
  };

  const currentRating = epcData.property_data.current_energy_rating;
  const potentialRating = epcData.property_data.potential_energy_rating || currentRating;

  // Calculate savings (this is simplified, real calculations would be more complex)
  let potentialSavings = 0;
  let ratingIndex = Object.keys(defaultSavingsByRating).indexOf(currentRating);
  const potentialIndex = Object.keys(defaultSavingsByRating).indexOf(potentialRating);

  while (ratingIndex > potentialIndex) {
    potentialSavings += defaultSavingsByRating[Object.keys(defaultSavingsByRating)[ratingIndex]];
    ratingIndex--;
  }

  // Generate recommendations
  const recommendations = [];

  if (epcData.property_data.recommendations) {
    return {
      potentialSavings,
      recommendations: epcData.property_data.recommendations,
    };
  }

  // Default recommendations if none provided
  if (currentRating <= 'D') {
    recommendations.push({
      improvement: 'Improve loft insulation',
      savingsEstimate: '£200-£300 per year',
      cost: '£300-£1,500',
    });

    recommendations.push({
      improvement: 'Install energy efficient lighting',
      savingsEstimate: '£30-£50 per year',
      cost: '£50-£100',
    });
  }

  return {
    potentialSavings,
    recommendations,
  };
};

/**
 * Get all energy efficiency data for a property
 *
 * @param {Object} address - Address details including postcode
 * @param {boolean} forceRefresh - Whether to force a refresh of the data (bypass cache)
 * @returns {Promise<Object>} - Combined energy data
 */
const getAllEnergyData = async (address, forceRefresh = false) => {
  try {
    console.log(
      '%c getAllEnergyData called with address:',
      'background: #3182CE; color: white; padding: 2px 6px; border-radius: 2px;',
      address,
      'forceRefresh:',
      forceRefresh,
    );

    if (!address || !address.postcode) {
      console.error(
        '%c No valid address/postcode provided to getAllEnergyData',
        'background: #E53E3E; color: white; padding: 2px 6px; border-radius: 2px;',
      );
      return {
        epcData: null,
        energySavings: {
          potentialSavings: 0,
          recommendations: [],
        },
        error: 'No valid address or postcode provided',
      };
    }

    const { epcData, error } = await getEpcData(address, forceRefresh);
    console.log(
      '%c getAllEnergyData - EPC data result:',
      'background: #2F855A; color: white; padding: 2px 6px; border-radius: 2px;',
      { epcData, error },
    );

    if (error || !epcData) {
      console.warn(
        '%c getAllEnergyData - No EPC data found:',
        'background: #DD6B20; color: white; padding: 2px 6px; border-radius: 2px;',
        error,
      );
      return {
        epcData: null,
        energySavings: {
          potentialSavings: 0,
          recommendations: [],
        },
        error: error || 'No energy data found',
      };
    }

    // When debugging, log the raw property data structure
    console.log(
      '%c getAllEnergyData - Property data structure:',
      'background: #805AD5; color: white; padding: 2px 6px; border-radius: 2px;',
      epcData.property_data ? Object.keys(epcData.property_data) : 'No property_data found',
    );

    const energySavings = calculateEnergySavings(epcData);
    console.log(
      '%c getAllEnergyData - Calculated energy savings:',
      'background: #2F855A; color: white; padding: 2px 6px; border-radius: 2px;',
      energySavings,
    );

    return {
      epcData,
      energySavings,
      error: null,
    };
  } catch (error) {
    console.error(
      '%c Error in getAllEnergyData:',
      'background: #E53E3E; color: white; padding: 2px 6px; border-radius: 2px;',
      error,
    );
    return {
      epcData: null,
      energySavings: {
        potentialSavings: 0,
        recommendations: [],
      },
      error: 'Failed to fetch energy data: ' + (error.message || error),
    };
  }
};

export { getEpcData, calculateEnergySavings, getAllEnergyData };
